import { useState, type FormEvent } from 'react';
import { verifyLinkOtp, type ExchangeResult, type OtpChallenge } from '@/api/capture';
import { SvApiError, SvNetworkError } from '@/api/errors';
import { PACK_GATED_LOCALES, chooserLocales, useT } from '@/i18n/runtime';
import { useCaptureStore } from '@/store/captureStore';
import { LanguageRows } from './LanguageChooser';
import { AppBar } from './components/AppBar';
import { Button } from './components/Button';
import { Icon } from './components/Icon';
import { CentredNotice } from './components/Shell';
import type { TenantConfig } from '@/theme/tenant';

/**
 * The OTP front door on the link (SPEC-15 / S15-1, contract v0.1.9 §C) — the second credential.
 *
 * The link opened the door; the code proves the person holding the link also holds the invited email or
 * phone. This screen sits BEFORE the confirm-request page: the representative sees "we sent a code to
 * r***@x.com", enters it, and only then sees who is asking and for what. Rules:
 *   • the code is sent once and never kept; a wrong code says how many tries remain (from the server's 4xx);
 *   • the fifth wrong code LOCKS THE LINK — that is terminal here ("the organisation must send a new one");
 *     nothing on this page can unlock it, because a re-invite is the server's act;
 *   • there is NO resend button: the contract has no resend endpoint, so the page says to ask the
 *     organisation rather than pretend a tap did something;
 *   • the anti-impersonation sentence is on this page too — no one will ask for this code by phone.
 * The tenant is not proven until the code is: the page renders in the neutral shell unless the challenge
 * carries a name/branding (additive on the wire; absent today).
 *
 * VC-12 r5: when the challenge's `packLocales` opens a pack-gated language (a Gulf pack: `[en, ar]`), the page offers
 * the language choice FIRST — the same rows as the chooser (`LanguageRows`) — and renders in the chosen language
 * (Arabic mirrors from the root). With no bearer yet the choice lives on this device; the front door persists it on
 * the case after the code is verified, so the customer is not asked again. An India challenge carries no
 * `packLocales`, and this page is exactly what it was.
 */
export function offersFrontDoorLanguage(packLocales: readonly string[] | null | undefined): boolean {
  return chooserLocales(packLocales).some((l) => PACK_GATED_LOCALES.includes(l));
}

export type OtpFailure =
  | { readonly kind: 'wrong'; readonly attemptsLeft: number | null }
  | { readonly kind: 'locked' }
  | { readonly kind: 'expired' }
  | { readonly kind: 'channelUnavailable' }
  | { readonly kind: 'unreachable' }
  | { readonly kind: 'other' };

function attemptsLeftOf(details: unknown): number | null {
  if (!details || typeof details !== 'object') return null;
  const d = details as Record<string, unknown>;
  for (const key of ['attemptsLeft', 'attemptsRemaining', 'remainingAttempts']) {
    const v = d[key];
    if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return Math.floor(v);
  }
  return null;
}

/**
 * Classify a verify/exchange refusal TOLERANTLY. The contract names only SV_OTP_CHANNEL_UNAVAILABLE; the be's
 * wrong/locked/expired codes are matched by the typed code first, then by the verbatim server code's shape,
 * then by status — so a be that names them differently still lands on the honest screen, never on a raw
 * "UNKNOWN". Re-pin to the exact codes when build/wire/sv-exchange-otp.json lands.
 */
export function otpFailureOf(e: unknown): OtpFailure {
  if (e instanceof SvNetworkError) return { kind: 'unreachable' };
  if (!(e instanceof SvApiError)) return { kind: 'other' };
  const raw = (e.serverCode ?? '').toUpperCase();
  if (e.code === 'SV_OTP_CHANNEL_UNAVAILABLE') return { kind: 'channelUnavailable' };
  if (e.code === 'SV_OTP_LOCKED' || e.code === 'SV_LINK_LOCKED' || e.status === 423 || /LOCKED/.test(raw)) return { kind: 'locked' };
  if (e.code === 'SV_OTP_EXPIRED' || e.code === 'SV_LINK_EXPIRED' || /EXPIRED/.test(raw)) return { kind: 'expired' };
  const attemptsLeft = attemptsLeftOf(e.details);
  if (attemptsLeft === 0) return { kind: 'locked' };
  if (e.code === 'SV_OTP_INVALID' || /OTP.*(INVALID|WRONG|MISMATCH)/.test(raw) || (e.status >= 400 && e.status < 500 && attemptsLeft !== null)) {
    return { kind: 'wrong', attemptsLeft };
  }
  if (e.status >= 400 && e.status < 500) return { kind: 'wrong', attemptsLeft: null };
  return { kind: 'other' };
}

export const OTP_LENGTH = 6;

export function OtpStep({
  challenge,
  tenant,
  blocked = null,
  onVerified,
}: {
  challenge: OtpChallenge | null;
  tenant: TenantConfig | null;
  /** A terminal state reached at the EXCHANGE (before any code existed): the link is locked, or the tenant's
   *  channel cannot deliver (503 SV_OTP_CHANNEL_UNAVAILABLE). */
  blocked?: 'locked' | 'channelUnavailable' | null;
  onVerified: (ex: ExchangeResult) => void;
}): JSX.Element {
  const { t, locale } = useT();
  const setFrontDoorLocale = useCaptureStore((s) => s.setFrontDoorLocale);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<OtpFailure | null>(null);

  const terminal: 'locked' | 'channelUnavailable' | 'expired' | null =
    blocked ?? (failure?.kind === 'locked' || failure?.kind === 'expired' || failure?.kind === 'channelUnavailable' ? failure.kind : null);

  if (terminal === 'locked') {
    return <CentredNotice title={t('sv.otp.locked.title')} body={t('sv.otp.locked.body')} />;
  }
  if (terminal === 'channelUnavailable') {
    return <CentredNotice title={t('sv.otp.unavailable.title')} body={t('sv.otp.unavailable.body')} />;
  }
  if (terminal === 'expired') {
    return <CentredNotice title={t('sv.otp.expired.title')} body={t('sv.otp.expired.body')} />;
  }
  if (!challenge) {
    return <CentredNotice title={t('sv.entry.notFound.title')} body={t('sv.entry.notFound.body')} />;
  }

  const ready = code.length === OTP_LENGTH && !busy;

  const submit = async (e?: FormEvent): Promise<void> => {
    e?.preventDefault();
    if (!ready) return;
    setBusy(true);
    setFailure(null);
    try {
      const ex = await verifyLinkOtp(challenge.otpId, code);
      onVerified(ex);
    } catch (err) {
      setFailure(otpFailureOf(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const failureText = ((): string | null => {
    if (!failure) return null;
    switch (failure.kind) {
      case 'wrong':
        if (failure.attemptsLeft === null) return t('sv.otp.wrong');
        if (failure.attemptsLeft === 1) return t('sv.otp.wrong.last');
        return t('sv.otp.wrong.attempts', { n: String(failure.attemptsLeft) });
      case 'unreachable':
        return t('sv.otp.unreachable');
      default:
        return t('sv.otp.failed');
    }
  })();

  return (
    <div className="app-view">
      {tenant ? <AppBar tenant={tenant} subtitle={t('sv.subtitle')} /> : null}
      <form className="stack" onSubmit={(e) => void submit(e)} data-testid="otp-form">
        {offersFrontDoorLanguage(challenge.packLocales) ? (
          <div className="card pad" data-testid="otp-language">
            <div className="pt" style={{ marginBottom: 6 }}>
              <Icon name="globe" size={16} /> {t('sv.lang.title')}
            </div>
            <LanguageRows packLocales={challenge.packLocales} current={locale} onPick={(picked) => setFrontDoorLocale(picked)} />
          </div>
        ) : null}
        <div>
          <div className="h-title sm">{t('sv.otp.title')}</div>
          <p className="sub-sm" style={{ marginTop: 6 }} data-testid="otp-sent">
            {t(challenge.channel === 'SMS' ? 'sv.otp.sent.SMS' : 'sv.otp.sent.EMAIL', { target: challenge.maskedTarget })}
          </p>
          <p className="sub-sm">{t('sv.otp.validFor')}</p>
        </div>

        <div className="card pad">
          <label className="pt" htmlFor="sv-otp-code">{t('sv.otp.label')}</label>
          <input
            id="sv-otp-code"
            className="sv-otp-input mono"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={OTP_LENGTH}
            value={code}
            disabled={busy}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
            data-testid="otp-input"
            aria-invalid={failure?.kind === 'wrong' ? true : undefined}
          />
          {failureText ? (
            <div className="sv-otp-msg" role="alert" data-testid="otp-failure">{failureText}</div>
          ) : null}
        </div>

        <p className="sv-never" role="note" data-testid="otp-never">
          <Icon name="lock" size={14} /> {t('sv.otp.never')}
        </p>
        <p className="sub-sm" data-testid="otp-resend">{t('sv.otp.resend')}</p>

        <div className="spacer" />
        <div className="stack-sm" style={{ marginTop: 16 }}>
          <Button type="submit" disabled={!ready} data-testid="otp-verify">
            {busy ? t('sv.otp.verifying') : t('sv.otp.verify')}
          </Button>
        </div>
      </form>
    </div>
  );
}
